import { Link } from "react-router-dom";
import { Instagram, Mail, Music2, Phone } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { Heart as HeartIcon, Scallop } from "./Decor";

export default function Footer() {
  const { settings } = useStore();
  const socials = [
    settings.social_instagram && { icon: Instagram, label: "Instagram", href: settings.social_instagram },
    settings.social_tiktok && { icon: Music2, label: "TikTok", href: settings.social_tiktok },
  ].filter(Boolean);

  return (
    <footer className="relative bg-white mt-[-1px]">
      <Scallop fill="#ffffff" dir="up" className="text-white" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-8">
        <div className="grid gap-10 md:grid-cols-4 items-start">
          <div>
            <Link to="/" data-testid="footer-logo-link">
              <img src="/assets/logo.jpg" alt="Hooked by Rachel" className="h-16 w-auto" />
            </Link>
            <p className="mt-3 text-ink/80 font-semibold">
              Handmade happiness,
              <br />
              one stitch at a time. <HeartIcon className="inline w-4 h-4 text-candy" />
            </p>
          </div>
          <nav aria-label="Explore">
            <h3 className="font-display font-semibold text-lg mb-3">Explore</h3>
            <ul className="space-y-2 text-ink/70 font-semibold">
              <li><Link to="/shop" data-testid="footer-shop-link" className="hover:text-candy transition-colors duration-200">Shop</Link></li>
              <li><Link to="/meet-rachel" data-testid="footer-meet-rachel-link" className="hover:text-candy transition-colors duration-200">Meet Rachel</Link></li>
              <li><Link to="/custom-orders" data-testid="footer-custom-link" className="hover:text-candy transition-colors duration-200">Custom Orders</Link></li>
              <li><a href={settings.site_url || "https://hookedbyrachel.com"} data-testid="footer-site-link" className="hover:text-candy transition-colors duration-200">www.hookedbyrachel.com</a></li>
            </ul>
          </nav>
          <nav aria-label="Help">
            <h3 className="font-display font-semibold text-lg mb-3">Help</h3>
            <ul className="space-y-2 text-ink/70 font-semibold">
              <li><Link to="/contact" data-testid="footer-contact-link" className="hover:text-candy transition-colors duration-200">Contact</Link></li>
              <li><Link to="/care-guide" data-testid="footer-care-link" className="hover:text-candy transition-colors duration-200">Care guide</Link></li>
              <li><Link to="/shipping-returns" data-testid="footer-shipping-link" className="hover:text-candy transition-colors duration-200">Shipping &amp; returns</Link></li>
              <li><Link to="/faq" data-testid="footer-faq-link" className="hover:text-candy transition-colors duration-200">FAQ</Link></li>
            </ul>
          </nav>
          <div className="flex flex-col gap-3">
            <a href={`tel:${(settings.phone || "").replace(/[^0-9+]/g, "")}`} data-testid="footer-phone-link" className="flex items-center gap-2 font-bold text-ink hover:text-candy transition-colors duration-200">
              <span className="w-8 h-8 rounded-full bg-candy-soft flex items-center justify-center"><Phone className="w-4 h-4 text-candy" /></span>
              {settings.phone || "310-881-5155"}
            </a>
            <a href={settings.site_url || "https://hookedbyrachel.com"} data-testid="footer-website-link" className="flex items-center gap-2 font-bold text-ink hover:text-candy transition-colors duration-200">
              <span className="w-8 h-8 rounded-full bg-candy-soft flex items-center justify-center text-candy font-display font-semibold">h</span>
              hookedbyrachel.com
            </a>
            {settings.social_email && (
              <a href={settings.social_email.startsWith("mailto:") ? settings.social_email : `mailto:${settings.social_email}`} data-testid="footer-email-link" className="flex items-center gap-2 font-bold text-ink hover:text-candy transition-colors duration-200">
                <span className="w-8 h-8 rounded-full bg-candy-soft flex items-center justify-center"><Mail className="w-4 h-4 text-candy" /></span>
                {settings.social_email.replace("mailto:", "")}
              </a>
            )}
            {socials.length > 0 && (
              <div className="flex gap-2 pt-1">
                {socials.map((s) => (
                  <a key={s.label} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} data-testid={`footer-social-${s.label.toLowerCase()}`} className="w-9 h-9 rounded-full bg-candy-soft hover:bg-candy-glow flex items-center justify-center transition-colors duration-200">
                    <s.icon className="w-4 h-4 text-candy-deep" />
                  </a>
                ))}
              </div>
            )}
            <a href={settings.site_url || "https://hookedbyrachel.com"} target="_blank" rel="noreferrer" data-testid="footer-qr-link" className="mt-1 inline-flex items-center gap-3 group w-fit">
              <img src="/assets/hookedbyrachel-home.svg" alt="QR code — scan to shop hookedbyrachel.com" className="w-16 h-16 rounded-lg border-2 border-candy-soft group-hover:border-candy transition-colors duration-200 bg-white p-1" />
              <span className="font-hand text-xl font-semibold text-candy-deep leading-tight">Scan<br />to shop</span>
            </a>
          </div>
        </div>
        <div className="mt-10 pt-5 border-t border-candy-soft/70 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-muted">
          <p data-testid="footer-copyright">
            <HeartIcon className="inline w-3.5 h-3.5 text-candy" /> © {new Date().getFullYear()} Hooked By Rachel <HeartIcon className="inline w-3.5 h-3.5 text-candy" />
          </p>
          <p data-testid="footer-designed-by">
            Designed by{" "}
            <a href={`mailto:${settings.designed_by_email || "Lawrence@masteraibots.com"}`} data-testid="footer-designed-by-link" className="font-bold text-candy-deep hover:underline">
              {settings.designed_by_name || "Lawrence Williams"}
            </a>
          </p>
          <Link to="/admin" data-testid="footer-admin-login" className="hover:text-candy transition-colors duration-200 font-semibold">Admin Login</Link>
        </div>
      </div>
    </footer>
  );
}
